<template>
  <div
    class="bg-white"
  >
    <div>
      <table class="table m-0 b-table b-table-fixed">
        <tr
          v-for="(campaign, index) in campaigns"
          :key="index"
        >
          <td
            ref="row-details-expand"
            class="row-details-expand"
          >
            <!-- Empty for Styling -->
          </td>
          <td
            ref="row-details-campaign-name"
            class="row-details-campaign-name text-wrap"
          >
            <span
              class="ellipsis"
              data-test="campaign-title"
              v-b-tooltip.hover.top.ds800
              :title="campaign.name"
            >
              <router-link
                v-if="campaign.shareTypes.includes('CAMPAIGN')"
                :data-test="getCampaignLinkParams(campaign).dataTest"
                class="large"
                :to="{
                  name: `${getCampaignLinkParams(campaign).name}`,
                  params: {
                    campaignId: campaign.id,
                    advertiserId: campaign.advertiserId
                  }
                }"
              >
                {{ campaign.name }}
              </router-link>
              <template v-else>{{ campaign.name }}</template>
            </span>
          </td>
          <td
            ref="row-details-state"
            class="row-details-state"
          >
            <div
              v-if="campaign.state"
            >
              <span
                :data-test="`campaign-status-${campaign.id}`"
              >
                <span :class="[getStatusIcon(campaign), getStatusColor(campaign)]" />
                {{ getColumnState(campaign) }}
              </span>
              <span
                v-if="campaign.shareTypes.includes('CAMPAIGN') && shouldShowPlayOrPauseButton(campaign)"
              >
                <a
                  href="#"
                  class="icon ml-3"
                  data-test="campaign-status-pause"
                  @click="updateCampaignState(campaign)"
                >
                  <i :class="['fa-light', campaign.state === 'LIVE' ? 'fa-pause' : 'fa-play']" />
                </a>
              </span>
            </div>
          </td>
          <td
            ref="row-details-start-date"
            class="row-details-start-date"
          >
            <small>{{ formatDateForPlatform(campaign.startDate) }}</small>
          </td>
          <td
            ref="row-details-end-date"
            class="row-details-end-date"
          >
            <small>{{ formatDateForPlatform(campaign.endDate) }}</small>
          </td>
          <td
            ref="row-details-budget"
            class="row-details-budget"
          >
            <div>
              <div class="clearfix">
                <div
                  class="float-left"
                  data-test="budget-spent-percentage"
                >
                  <strong>{{ getBudgetSpentPercentage(campaign.budgetRemaining) || '0' }}%</strong>
                </div>
              </div>
              <div class="progress progress-xs">
                <div
                  class="progress-bar"
                  :class="setProgressColor(getBudgetSpentPercentage(campaign.budgetRemaining)
                    || 0)"
                  role="progressbar"
                  :style="`width: ${getBudgetSpentPercentage(campaign.budgetRemaining) || '0'}%`"
                  :aria-valuenow="getBudgetSpentPercentage(campaign.budgetRemaining) || '0'"
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>
          </td>
          <td
            ref="row-details-impressions"
            class="row-details-impressions"
          >
            {{ campaign.impressions }}
          </td>
          <td
            ref="row-details-clicks"
            class="row-details-clicks"
          >
            {{ campaign.clicks }}
          </td>
          <td
            ref="row-details-ctr"
            class="row-details-ctr"
          >
            {{ campaign.ctr > 0 ? `${campaign.ctr}%` : '%' }}
          </td>
          <td
            ref="row-details-actions"
            class="row-details-actions"
          >
            <CampaignShareActions
              :index="index"
              :item="campaign"
              @refreshTable="refreshTable"
            />
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>

import dateHelper from '@/core/mixins/dateHelper';
import CampaignShareActions from '@/modules/campaigns/components/CampaignShareActions';

import * as types from '@/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';

import tableMixin from '@/modules/campaigns/mixins/tableMixin';
import { mapActions, mapMutations } from 'vuex';

export default {
  name: 'SharedCampaignsTable',
  components: {
    CampaignShareActions,
  },
  mixins: [
    dateHelper,
    tableMixin,
  ],
  props: {
    headers: {
      type: Array,
      required: true,
    },
    campaigns: {
      type: Array,
      required: true,
    },
    sharedCampaignList: {
      type: Array,
      required: true,
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.SET_CAMPAIGN_STATE,
    ]),
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    shouldShowPlayOrPauseButton(campaign) {
      return campaign.state === 'LIVE' || campaign.state === 'PAUSED';
    },
    updateCampaignState(item) {
      this.isBusy = true;
      this.SET_CAMPAIGN_STATE({
        advertiserId: item.advertiserId,
        campaignId: item.id,
        state: item.state === 'LIVE' ? 'PAUSED' : 'LIVE',
      })
        .then((response) => {
          this.isBusy = false;
          this.campaigns
            .find((campaign) => campaign.id === response.id).state = response.state;
          this.SET_GLOBAL_MESSAGE({
            message: `Campaign was set to ${item.state.toLowerCase()}.`,
            type: 'info',
          });
          this.refreshTable();
        })
        .catch(() => {
          this.isBusy = false;
          this.SET_GLOBAL_MESSAGE({
            message: 'Campaign state could not be updated',
            type: 'danger',
          });
        });
    },
    refreshTable() {
      this.$emit('refreshTable');
    },
  },
};
</script>

<style lang="scss" scoped>
.row-details-expand {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-expand-width) + calc(0.75rem * 2));
}

.row-details-campaign-name {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-campaign-name-width)  + calc(0.75rem * 2));
}

.row-details-state {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-state-width)  + calc(0.75rem * 2));
}

.row-details-start-date {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-start-date-width) + calc(0.75rem * 2));
}

.row-details-end-date {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-end-date-width) + calc(0.75rem * 2));
}

.row-details-budget {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-budget-width)  + calc(0.75rem * 2));
}

.row-details-impressions {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-impressions-width) + calc(0.75rem * 2));
}

.row-details-clicks {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-clicks-width) + calc(0.75rem * 2));
}

.row-details-ctr {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-ctr-width) + calc(0.75rem * 2));
}

.row-details-actions {
  // + calc(0.75rem * 2) because header has padding, and removing this causes problems.
  width: calc(var(--row-details-actions-width) + calc(0.75rem * 2));
}
</style>
